/* eslint-disable @typescript-eslint/no-unused-vars */
import {useEffect, useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';
import Big from 'big.js';

import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreFieldMetadata,
  FormikStoreForm,
  FormikStoreObject,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';

import {
  CreditTransaction,
  CreditType,
  ExchangeRate,
  Fund,
  LimitedPartner,
  Property,
  Reservation,
  useCreditTypes,
  useExchangeRates,
  useFunds,
  useProperties,
} from '../../hooks/useDomainData';
import {useMutation, useQueryClient} from 'react-query';
import {getCreditBalancesByLPIdByFundByType} from '../CreditBalances';
import {useAuthPersonInfo} from '../..//hooks/usePersonInfo';
import {useAlerts} from 'react-native-paper-alerts';

export interface ReservationEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  allLimitedPartners: ReadonlyArray<LimitedPartner>;
  transactions?: ReadonlyArray<CreditTransaction>;
  reservation?: Reservation;
}

export function ReservationEditor(props: ReservationEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const properties = useProperties();
  const creditTypes = useCreditTypes();
  const exchangeRates = useExchangeRates();
  const funds = useFunds();
  const authPersonInfo = useAuthPersonInfo();

  const [validating, setValidating] = useState(false);

  // console.log('fetching credit balances');
  const balancesByLPByFundByType = getCreditBalancesByLPIdByFundByType(
    props.transactions,
  );

  const standardReservationDurationMillis = 7 * 24 * 60 * 60 * 1000;
  let defaultReservationEndDate = new Date(
    Date.now() + standardReservationDurationMillis,
  );
  let reservation: {[key: string]: FormikStoreType} = {
    reservationId: Date.now(),
    limitedPartnerId:
      Array.from(props.allLimitedPartners).length === 1
        ? Array.from(props.allLimitedPartners)[0].ref
        : undefined,
    propertyId: undefined,
    reservationCreatedDate: new Date(),
    reservationStartDate: new Date(),
    reservationEndDate: defaultReservationEndDate,
    requiredCredits: undefined,
  };

  if (props.reservation !== undefined) {
    // console.log(`init from res ${props.reservation.ref.path}`);
    reservation.reservationId = props.reservation.reservationId;
    reservation.limitedPartnerId = props.reservation.limitedPartnerId;
    reservation.propertyId = props.reservation.propertyId;
    if (props.reservation.reservationCreatedDate !== undefined) {
      reservation.reservationCreatedDate =
        props.reservation.reservationCreatedDate;
    }
    reservation.reservationStartDate =
      props.reservation.reservationStartDate.toDate();
    reservation.reservationEndDate =
      props.reservation.reservationEndDate.toDate();

    // FIXME - if house fund and limited partner fund are different,
    // we need to translate requiredCredits from limited partner fund to house fund
    reservation.requiredCredits = props.reservation.requiredCredits;
  } else {
    // console.log('no reservation prop passed, default init');
  }

  function getFundCodeForProperty(
    propertyId: FirebaseFirestoreTypes.DocumentReference<Property>,
  ) {
    const property = properties?.find(
      prop => prop.ref.path === propertyId.path,
    );
    return property?.fund?.fundCode ?? '';
  }
  let propertyFundCode = props.reservation
    ? getFundCodeForProperty(props.reservation.propertyId)
    : '';

  let applicableExchangeRate: ExchangeRate | undefined;
  function getApplicableExchangeRate(
    balanceCreditType?: CreditType,
    propertyFund?: Fund,
    applicableRangeStart?: Date,
    applicableRangeEnd?: Date,
  ): ExchangeRate | undefined {
    if (!balanceCreditType || !propertyFund) {
      return undefined;
    }
    if (balanceCreditType.fundId.path !== propertyFund?.ref.path) {
      applicableExchangeRate = exchangeRates?.find(rate => {
        if (rate.originFundId.path !== balanceCreditType.fundId.path) {
          // console.log('rate origin !== credit fund. Skipping entry.');
          return false;
        }
        if (rate.destinationFundId.path !== propertyFund.ref.path) {
          // console.log('rate dest !== property fund. Skipping entry');
          return false;
        }

        // console.log(
        //   'current res dates: ' +
        //     applicableRangeStart +
        //     '/' +
        //     applicableRangeEnd,
        // );
        // FIXME add date validation back after getting form working again
        if (
          (applicableRangeEnd !== undefined &&
            rate.dateRangeStart.toDate() > applicableRangeEnd) ||
          (applicableRangeStart !== undefined &&
            rate.dateRangeEnd.toDate() < applicableRangeStart)
        ) {
          // console.log(
          //   'exchange rate date range out of range for reservation',
          // );
          return false;
        }

        // console.log(
        //   'found applicable rate: ' +
        //     rate.ref.path +
        //     ' (ratio ' +
        //     rate.exchangeRate +
        //     ')',
        // );
        return true;
      });
      return applicableExchangeRate;
    }
  }

  const limitedPartnerFormikStoreMetadata: FormikStoreFieldMetadata = {
    key: 'limitedPartnerId',
    name: 'Limited Partner',
    type: FormikStoreTypes.select,
    required: true,
    editable: authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
    errorText: 'Limited Partner is a required field',

    selectItems: {
      documents: Array.from(props.allLimitedPartners),
      items: Array.from(props.allLimitedPartners).map(
        limitedPartner => limitedPartner.ref,
      ),
      labelExtractor: limitedPartner => limitedPartner?.limitedPartnerName,
    },
    validator: (value, required, formikStoreObjects) => {
      // toggle property based on whether we have an LP selected
      formikStoreObjects[0].fieldMetadata.forEach(metadata => {
        if (metadata.key === 'propertyId') {
          metadata.visible = value !== undefined;
        }
      });
      return DefaultFormikStoreValidator(value, required, formikStoreObjects);
    },
  };

  const requiredCreditsFormikStoreMetadata: FormikStoreFieldMetadata = {
    key: 'requiredCredits',
    name: `Required${' ' + propertyFundCode + ' '}credits`,
    type: FormikStoreTypes.number,
    required: true,
    editable: authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
    visible: props.reservation ? true : false, // TODO toggle this true when LP+Property are known and LP has credits === true
    errorText: 'Please enter the total required credits',
    validator: DefaultFormikStoreValidator,
  };

  // "LP has no credits" placeholder FormikStore object at index 1
  const noCreditsFormikStoreObject: FormikStoreObject = {
    title: 'LP has no credits.',
    firestoreObject: {
      credits: 0,
    },
    fieldMetadata: [
      {
        key: 'noAvailableCredits',
        name: 'LP has no available credits',
        type: FormikStoreTypes.number,
        required: false,
        editable: false,
        visible: false,
        validator: (
          value: FormikStoreType,
          required: boolean,
          formikStoreObjects: FormikStoreObject[],
        ) => {
          formikStoreObjects[1].firestoreObject.credits = value;
          return true;
        },
        errorText: 'No available credits',
      },
    ],
  };

  // "Total Credits Entered" entry FormikStore object at index 2
  const creditTotalFormikStoreObject: FormikStoreObject = {
    title: 'Credit Transaction Total',
    firestoreObject: {
      creditTransactionTotal: props.reservation
        ? props.reservation.requiredCredits
        : 0,
    },
    fieldMetadata: [
      {
        key: 'creditTransactionTotal',
        name: `Total entered${' ' + propertyFundCode + ' '}credits`,
        type: FormikStoreTypes.number,
        required: false,
        editable: false,
        visible: props.reservation ? true : false,
        alwaysValidate: true,
        validator: (
          value: FormikStoreType,
          required: boolean,
          formikStoreObjects: FormikStoreObject[],
        ) => {
          // Get our property fund
          const propertyId = formikStoreObjects[0].firestoreObject
            .propertyId as FirebaseFirestoreTypes.DocumentReference<Property>;
          const property = properties?.find(
            prop => prop.ref.path === propertyId?.path,
          );
          const propertyFund = property?.fund;

          // - iterate on all non-aggregate credit transaction firestore objects to obtain total
          let creditTotal = Big(0);
          Big.RM = Big.roundDown;
          for (let i = 3; i < formikStoreObjects.length; i++) {
            let fsObject = formikStoreObjects[i]
              .firestoreObject as Partial<CreditTransaction>;

            const creditTypeRef =
              fsObject.creditTypeId as FirebaseFirestoreTypes.DocumentReference<CreditType>;
            const creditType = creditTypes?.find(
              type => type.ref.path === creditTypeRef.path,
            );
            const creditTypeFund = creditType?.fund;

            let currentExchangeRate;
            if (creditTypeFund?.ref.path !== propertyFund?.ref.path) {
              // console.log('calculator fund credit conversion needed');
              if (!creditType?.allowFCC) {
                // console.log(
                //   'calculator FCC needed but not allowed. Purging credits.',
                // );
                fsObject.credits = undefined;
                // console.log(
                //   'zeroing ' +
                //     'credits' +
                //     creditTypeFund?.fundCode +
                //     creditType?.creditType,
                // );
                // FIXME this is not zeroing correctly after switching propertyId
                // @ts-ignore
                fsObject[
                  'credits' + creditTypeFund?.fundCode + creditType?.creditType
                ] = undefined;
              }

              currentExchangeRate = getApplicableExchangeRate(
                creditType,
                property?.fund,
              )?.exchangeRate;
            }

            // console.log(
            //   'totalling with credit fund: ' + creditTypeFund?.fundCode,
            // );

            // console.log(`credits ${i}: ${fsObject.credits}`);
            if (fsObject.credits) {
              let exchangeRate = Big(1);
              if (currentExchangeRate) {
                // console.log(`rate ${i}: ${fsObject.exchangeRate}`);
                exchangeRate = Big(currentExchangeRate as string);
              }
              let exchangedCredits = Big(fsObject.credits as string).times(
                exchangeRate,
              );
              // console.log(`exchange val ${i}: ${exchangedCredits}`);

              creditTotal = creditTotal.add(exchangedCredits);
              // console.log(`total post-${i}: ${creditTotal}`);
            }
          }

          // Now, after doing all the exchanges, truncate precision to two places via normal rounding
          creditTotal = creditTotal.round(2, Big.roundHalfUp);
          // ...then if the credits aren't enough, we round down
          creditTotal = creditTotal.round(0, Big.roundDown);

          // - set total into the aggregate credit transaction counter
          formikStoreObjects[2].firestoreObject.creditTransactionTotal =
            creditTotal.toString();
          // console.log(
          //   `final total: ${formikStoreObjects[2].firestoreObject.creditTransactionTotal}`,
          // );

          const totalCredits =
            formikStoreObjects[2].firestoreObject.creditTransactionTotal;
          const requiredCredits =
            formikStoreObjects[0].firestoreObject.requiredCredits;
          // console.log('validating: ' + totalCredits);
          // console.log('currently required credits is ' + requiredCredits);
          // FIXME comparing them as strings right now. Maybe compare as real numbers?
          if (
            totalCredits === undefined ||
            requiredCredits === undefined ||
            totalCredits + '' !== requiredCredits + ''
          ) {
            // console.log('validated: undefined or missing required credits.');
            return false;
          }
          // console.log('we have the required credits!');
          return true;
        },
        errorText: 'Credits entered do not equal required credits.',
      },
    ],
  };

  // If we have a reservation, filter down to just the related credit transactions
  let reservationCreditTransactions: CreditTransaction[] = [];
  props.transactions?.map(txn => {
    // console.log(`txn ${txn.ref.path} res ${txn.reservationId?.path}`);
    if (
      props.reservation !== undefined &&
      txn.reservationId?.path === props.reservation?.ref.path
    ) {
      // console.log(`found txn ${txn.ref.path} for res`);
      reservationCreditTransactions.push(txn);
    }
  });

  // Credit Transactions are one FormikStore object each at index 3+
  let creditTransactionObjects: FormikStoreObject[] = [];
  creditTypes?.forEach(creditType => {
    const fundName = creditType.fund?.fundCode;
    const creditTypeName = creditType.creditType;

    // Skip if the credit type may not be used for reservations
    if (!creditType.allowReservation) {
      return;
    }

    // If we have a reservation and property fund !== credit type fund, get exchange rate
    if (
      props.reservation !== undefined &&
      creditType.fund?.ref.path !== props.reservation?.property?.fund?.ref.path
    ) {
      console.log(
        'have reservation and creditType ' +
          creditType.ref.path +
          ' has different fund then property.',
      );
      applicableExchangeRate = getApplicableExchangeRate(
        creditType,
        props.reservation?.property?.fund,
      );
      console.log('...so exchange rate is ' + applicableExchangeRate);
    }

    // Either match up existing transaction from a reservation, or make a new clean one
    let txnFromReservation = false;
    let firestoreObject: {[key: string]: any} | undefined;
    let existingTransaction = reservationCreditTransactions.find(
      txn => txn.creditTypeId.path === creditType.ref.path,
    );
    if (existingTransaction !== undefined) {
      firestoreObject = {
        creditTransactionId: existingTransaction.creditTransactionId,
        allocationDate: existingTransaction.allocationDate,
        limitedPartnerId: existingTransaction.limitedPartnerId,
        limitedPartner: existingTransaction.limitedPartner,
        creditTypeId: existingTransaction.creditTypeId,
        creditType: existingTransaction.creditType,
        exchangeRate: applicableExchangeRate?.exchangeRate,
        credits: existingTransaction.credits,
        creditBalance: 0,
        reservationId: existingTransaction.reservationId,
        transactionType: existingTransaction.transactionType,
        ref: existingTransaction.ref,
      };
    }
    if (firestoreObject === undefined) {
      // console.log(
      //   'making new txn object for creditType ' + creditType.ref.path,
      // );
      firestoreObject = {
        creditTranactionId: Date.now(), // FIXME if we make 5 at once they'll be the same!
        allocationDate: new Date(),
        limitedPartnerId: props.reservation?.limitedPartnerId,
        creditTypeId: creditType.ref,
        exchangeRate: applicableExchangeRate?.exchangeRate,
        creditBalance: 0,
      };

      if (props.reservation !== undefined) {
        const lpId = props.reservation.limitedPartner!.ref.id;
        const fund = creditType.fund!.fund;
        const type = creditType.creditType;
        if (
          balancesByLPByFundByType[lpId] !== undefined &&
          balancesByLPByFundByType[lpId][fund] !== undefined &&
          balancesByLPByFundByType[lpId][fund][type] !== undefined
        ) {
          firestoreObject.creditBalance =
            balancesByLPByFundByType[lpId][fund][type];
        }
      }

      firestoreObject['credits' + fundName + creditTypeName] = undefined;
    } else {
      // console.log(
      //   'res txn  ' + firestoreObject.ref.path + ' type ' + creditType.ref.path,
      // );
      txnFromReservation = true;

      // If we have an existing transaction, set the credits in and for validation to work, we need
      // to also add the credits used here to the balance for this credittype + LP
      firestoreObject['credits' + fundName + creditTypeName] =
        firestoreObject.credits * -1;
      // console.log(
      //   `adding ${firestoreObject.credits * -1} to balance for ${
      //     firestoreObject.limitedPartner.ref.id
      //   } / ${creditType.fund?.fund} / ${creditType.creditType}`,
      // );
      // The balance may not exist in the data structure, scaffold it up if necessary
      const lpId = firestoreObject.limitedPartner.ref.id;
      const fundId = creditType.fund!.fund;
      const type = creditType.creditType;
      if (balancesByLPByFundByType[lpId] === undefined) {
        balancesByLPByFundByType[lpId] = {};
      }
      const balancesByFundByType = balancesByLPByFundByType[lpId];
      if (balancesByFundByType[fundId] === undefined) {
        balancesByFundByType[fundId] = {};
      }
      if (balancesByFundByType[fundId][type] === undefined) {
        balancesByFundByType[fundId][type] = 0;
      }

      balancesByFundByType[fundId][type] += firestoreObject.credits * -1;
      firestoreObject.creditBalance = balancesByFundByType[fundId][type];
      // console.log(JSON.stringify(balancesByLPByFundByType));
    }

    creditTransactionObjects.push({
      title: 'credit type name here: ',
      firestoreObject,
      fieldMetadata: [
        {
          key: 'credits' + fundName + creditTypeName,
          name:
            fundName +
            ' ' +
            creditTypeName +
            ' (' +
            firestoreObject.creditBalance +
            ' available' +
            (applicableExchangeRate
              ? ', rate 1:' + applicableExchangeRate.exchangeRate
              : '') +
            ')',
          type: FormikStoreTypes.number,
          required: false,
          editable: authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
          visible: txnFromReservation || firestoreObject.creditBalance !== 0,
          validator: (
            value: FormikStoreType,
            required: boolean,
            formikStoreObjects: FormikStoreObject[],
          ) => {
            // - our key is fabricated, but starts with credits. Put our fabricated amount in to actual credits
            // FIXME if you switch property to a conversion and back, these are not erased
            // console.log(
            //   'thumping credit with ' +
            //     value +
            //     ' from ' +
            //     'credits' +
            //     fundName +
            //     creditTypeName,
            // );
            firestoreObject!.credits = value;
            // console.log('this credit entry is ' + value);

            // - make sure they have not entered more credits than they actually have
            if (value) {
              if (isNaN(+value) || +value > firestoreObject!.creditBalance) {
                return false;
              }
            }
            return true;
          },
          errorText:
            'Enter credits as a number, less than or equal to this balance',
        },
      ],
    });
  });

  const propertyFormikStoreMetadata: FormikStoreFieldMetadata = {
    key: 'propertyId',
    name: 'Property',
    type: FormikStoreTypes.select,
    required: true,
    editable: authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
    visible:
      props.allLimitedPartners.length === 1 || props.reservation !== undefined
        ? true
        : false,
    errorText: 'Property is a required field',
    selectItems: {
      documents: properties,
      items: properties?.map(property => property.ref) ?? [],
      labelExtractor: property => property?.propertyName,
    },
    derivedViewModelKeysToClear: creditTransactionObjects.map(
      creditTransactionObject => {
        return creditTransactionObject.fieldMetadata[0].key;
      },
    ),
    validator: (value, required, formikStoreObjects) => {
      const lpId = formikStoreObjects[0].firestoreObject
        .limitedPartnerId as FirebaseFirestoreTypes.DocumentReference<LimitedPartner>;
      const propertyId = formikStoreObjects[0].firestoreObject
        .propertyId as FirebaseFirestoreTypes.DocumentReference<Property>;
      const property = properties?.find(
        prop => prop.ref.path === propertyId?.path,
      );

      let hasAvailableCredits = false;
      const balancesByFundByType =
        lpId !== undefined
          ? balancesByLPByFundByType[
              (lpId as FirebaseFirestoreTypes.DocumentReference).id
            ]
          : {};

      if (lpId !== undefined && propertyId !== undefined) {
        // console.log('Editing visibility on rest of form; we have LP+property');
        // console.log('property is ' + propertyId.path);
        // console.log('LP is ' + lpId.path);

        hasAvailableCredits =
          balancesByFundByType !== undefined &&
          Object.keys(balancesByFundByType).length !== 0;

        // get name for credit total and credit transactions
        propertyFundCode = getFundCodeForProperty(propertyId);

        // Set up rest of form since we have LP and Property now:
      } else {
        // console.log('Toggling off below property, missing LP y/o property');
      }
      // console.log('hasAavailableCredits? ' + hasAvailableCredits);
      // console.log(
      //   'existing reservation ? ' + (props.reservation !== undefined),
      // );

      // If new reservation && can determine credits && there are no credits, toggle warning on
      formikStoreObjects[1].fieldMetadata.forEach(metadata => {
        if (metadata.key === 'noAvailableCredits') {
          // console.log('toggling on no credits warning?');
          metadata.visible =
            lpId !== undefined &&
            propertyId !== undefined &&
            !hasAvailableCredits &&
            props.reservation === undefined;
        }
      });

      // If existing reservation OR can determine credits and there are some, toggle required + credit total on
      formikStoreObjects[0].fieldMetadata.forEach(metadata => {
        if (metadata.key === 'requiredCredits') {
          metadata.name = `Required ${propertyFundCode} credits`;
          metadata.visible =
            lpId !== undefined &&
            propertyId !== undefined &&
            (hasAvailableCredits || props.reservation !== undefined);
        }
      });
      formikStoreObjects[2].fieldMetadata.forEach(metadata => {
        if (metadata.key === 'creditTransactionTotal') {
          metadata.name = `Total entered ${propertyFundCode} credits`;
          metadata.visible =
            lpId !== undefined &&
            propertyId !== undefined &&
            (hasAvailableCredits || props.reservation !== undefined);
        }
      });
      // console.log(
      //   'current balances: ' + JSON.stringify(balancesByFundByType, null, 2),
      // );
      for (let i = 3; i < formikStoreObjects.length; i++) {
        formikStoreObjects[i]?.fieldMetadata.forEach(metadata => {
          // TODO rename it with exchange rate as applicable
          // TODO figure out if the credits even apply, only show those that apply
          // console.log(
          //   'firestoreObject ' +
          //     i +
          //     JSON.stringify(formikStoreObjects[i].firestoreObject, null, 2),
          // );
          const creditTypeRef = formikStoreObjects[i].firestoreObject
            .creditTypeId as FirebaseFirestoreTypes.DocumentReference<CreditType>;
          const creditType = creditTypes?.find(
            type => type.ref.path === creditTypeRef.path,
          );
          const creditTypeFund = creditType?.fund;
          // console.log(
          //   `working on ${creditTypeFund?.fundCode} ${creditType?.creditType}`,
          // );

          // check deeply for a valid balance for this credit type
          let creditBalance = 0;
          if (
            creditTypeFund !== undefined &&
            creditType !== undefined &&
            balancesByFundByType[creditTypeFund.fund] !== undefined &&
            balancesByFundByType[creditTypeFund.fund][creditType.creditType] !==
              undefined
          ) {
            creditBalance =
              balancesByFundByType[creditTypeFund.fund][creditType.creditType];
          }
          metadata.visible =
            lpId !== undefined &&
            propertyId !== undefined &&
            // visible if either credit transaction from existing reservation or we have credits
            ((props.reservation !== undefined &&
              formikStoreObjects[i].firestoreObject.ref !== undefined) ||
              creditBalance !== 0);

          if (metadata.visible) {
            // we know we have a valid balance, interpolate name, balance, exchange rate
            // set the exchangeRate and creditBalance ephemeral keys on firestoreObject / CreditTransaction
            formikStoreObjects[i].firestoreObject.creditBalance = creditBalance;

            // If we have a reservation and property fund !== credit type fund, get exchange rate
            let currentExchangeRate: ExchangeRate | undefined;
            if (
              property !== undefined &&
              creditType!.fund?.ref.path !== property?.fund?.ref.path
            ) {
              // If we are visible, but it would require fund credit conversion,
              // make sure it is possible. If not, zero credits and mark not visible
              if (!creditType?.allowFCC) {
                // console.log(
                //   'credit type does not allowFCC? ' + creditType?.allowFCC,
                // );
                metadata.visible = false;
                // formikStoreObjects[i].firestoreObject[
                //   'credits' +
                //     creditType?.fund?.fundCode +
                //     creditType?.creditType
                // ] = undefined;
                // formikStoreObjects[i].firestoreObject.credits = undefined;

                // console.log(
                //   'creditTransaction currently ' +
                //     JSON.stringify(
                //       formikStoreObjects[i].firestoreObject,
                //       null,
                //       2,
                //     ),
                // );
                return;
              }

              // console.log(
              //   'have reservation and creditType ' +
              //     creditType.ref.path +
              //     ' has different fund then property.',
              // );
              currentExchangeRate = getApplicableExchangeRate(
                creditType,
                property?.fund,
              );
              // console.log('...so exchange rate is ' + applicableExchangeRate);
            }
            metadata.name =
              creditTypeFund?.fundCode +
              ' ' +
              creditType?.creditType +
              ' (' +
              creditBalance +
              ' available' +
              (currentExchangeRate
                ? ', rate 1:' + currentExchangeRate.exchangeRate
                : '') +
              ')';
          }
        });
      }

      return DefaultFormikStoreValidator(value, required, formikStoreObjects);
    },
  };

  const saveReservation = useMutation(
    (formObjects: FormikStoreObject[]) => {
      const firestoreObject = formObjects[0]
        .firestoreObject as unknown as Reservation;

      if (firestoreObject.limitedPartnerId === undefined) {
        throw new Error('Undefined limited partner, invalid reservation');
      }
      if (firestoreObject.propertyId === undefined) {
        throw new Error('Undefined propertyId, invalid reservation');
      }
      firestoreObject.reservationStartDate =
        firestoreObject.reservationStartDate as unknown as Date as unknown as FirebaseFirestoreTypes.Timestamp;
      firestoreObject.reservationEndDate =
        firestoreObject.reservationEndDate as unknown as Date as unknown as FirebaseFirestoreTypes.Timestamp;

      if (props.reservation === undefined) {
        return firestore().collection('reservations').add(firestoreObject);
      }
      // console.log(
      //   'attempting to save res: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection('reservations')
        .doc(props.reservation?.ref.id)
        .set(firestoreObject, {merge: true}) as Promise<unknown>;
    },
    {
      onSuccess: async (result, variables) => {
        // console.log('saving reservation succeeded. Saving transaction now');
        await queryClient.invalidateQueries('reservations');
        const reservationRef =
          props.reservation !== undefined
            ? props.reservation.ref
            : (result as FirebaseFirestoreTypes.DocumentReference<Reservation>);
        // console.log('Reservation result was: ' + reservationRef.path);
        // console.log('Original form contents: ' + JSON.stringify(variables));
        const firestoreObject = variables[0]
          .firestoreObject as unknown as Reservation;

        const limitedPartnerId = firestoreObject.limitedPartnerId;
        if (limitedPartnerId === undefined) {
          throw new Error(
            'undefined limited partner, invalid credit transaction',
          );
        }

        if (
          !isNaN(+firestoreObject.requiredCredits) &&
          +firestoreObject.requiredCredits === 0
        ) {
          // console.log('no credits required, no credit transaction');
          return;
        }

        // Iterate through the rest of the transactions making credit transactions...
        // TODO need to handle existing transactions that should update or delete
        for (let i = 3; i < variables.length; i++) {
          const creditTransaction = variables[i]
            .firestoreObject as unknown as CreditTransaction;
          const creditsUsed = creditTransaction.credits;

          if (creditTransaction.ref !== undefined) {
            // console.log(
            //   'existing txn ' + creditTransaction.ref.path + ', update',
            // );
            const cleanTransaction: Partial<CreditTransaction> = {
              limitedPartnerId: creditTransaction.limitedPartnerId,
              credits: '-' + creditTransaction.credits,
            };
            await firestore()
              .collection('creditTransactions')
              .doc(creditTransaction.ref.id)
              .set(cleanTransaction, {merge: true});
          } else if (
            creditsUsed !== undefined &&
            !isNaN(+creditsUsed) &&
            +creditsUsed !== 0
          ) {
            const transaction: Partial<CreditTransaction> = {
              creditTransactionId: Date.now(),
              allocationDate: new Date(),
              limitedPartnerId: limitedPartnerId,
              reservationId: reservationRef,
              // - credits taken (note we need to iterate on all the credit types used and do one txn per type, with exchange)
              credits: '-' + creditsUsed, // negate the credits in the transaction FIXME depending on type?
              creditTypeId: variables[i].firestoreObject
                .creditTypeId as FirebaseFirestoreTypes.DocumentReference<CreditType>,
              transactionType: 'Reservation',
            };
            // console.log(
            //   'adding transaction for credit type ' +
            //     transaction.creditTypeId?.path,
            // );
            await firestore().collection('creditTransactions').add(transaction);
          }
        }
        // },
        // onSettled: async () => {
        // console.log('saving reservation and transaction succeeded');
        return queryClient.invalidateQueries('creditTransactions');
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  return (
    <Portal>
      <Modal
        // style={[styles.container, styles.center]}
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.reservation ? 'Edit' : 'Create'} Reservation`,
              firestoreObject: reservation,
              fieldMetadata: [
                {
                  key: 'reservationStartDate',
                  name: 'Start Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText: 'Start date is a required field',
                  datesUTC: true,
                  validator: (value, required, formikStoreObjects) => {
                    // If reservation is standard duration (known because it is the
                    // default end date based on standard duration), shift end date
                    // with start date as a UI convenience
                    if (
                      formikStoreObjects[0].firestoreObject
                        .reservationEndDate === defaultReservationEndDate
                    ) {
                      formikStoreObjects[0].firestoreObject.reservationEndDate =
                        new Date(
                          (value as Date).valueOf() +
                            standardReservationDurationMillis,
                        );
                      defaultReservationEndDate =
                        formikStoreObjects[0].firestoreObject
                          .reservationEndDate;
                    }

                    // setCurrentStartDate(value as Date);
                    return DefaultFormikStoreValidator(
                      value,
                      required,
                      formikStoreObjects,
                    );
                  },
                },
                {
                  key: 'reservationEndDate',
                  name: 'End Date',
                  type: FormikStoreTypes.date,
                  required: true,
                  editable:
                    authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations,
                  errorText:
                    'End date is required and must be after start date.',
                  datesUTC: true,
                  validator: (value, required, formikStoreObjects) => {
                    // setCurrentEndDate(value as Date);
                    // FIXME add back date validation after getting form working again
                    // if (
                    //   currentStartDate !== undefined &&
                    //   currentEndDate !== undefined &&
                    //   currentEndDate < currentStartDate
                    // ) {
                    //   return false;
                    // }
                    return DefaultFormikStoreValidator(
                      value,
                      required,
                      formikStoreObjects,
                    );
                  },
                },
                limitedPartnerFormikStoreMetadata,
                propertyFormikStoreMetadata,
                requiredCreditsFormikStoreMetadata,
              ],
            },
            noCreditsFormikStoreObject,
            creditTotalFormikStoreObject,
            ...creditTransactionObjects,
          ]}
          buttons={
            authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations
              ? [
                  {
                    text: 'Cancel',
                    validate: false,
                    onPress: () => {
                      props.setDialogVisible(false);
                    },
                  },
                  {
                    text: 'Save',
                    validate: true,
                    onPress: formObjects => {
                      setValidating(true);
                      saveReservation.mutate(formObjects);
                      props.setDialogVisible(false);
                    },
                  },
                ]
              : [
                  {
                    text: 'Close',
                    validate: false,
                    onPress: () => {
                      props.setDialogVisible(false);
                    },
                  },
                ]
          }
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
